import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { AlertType } from "@/hooks/useInlineAlert";

interface Favorite {
  id: string;
  product_id: string;
  notify_on_discount: boolean;
  created_at: string;
}

export interface FavoritesAlert {
  type: AlertType;
  message: string;
}

interface FavoritesContextType {
  favorites: Favorite[];
  loading: boolean;
  isFavorite: (productId: string) => boolean;
  toggleFavorite: (productId: string) => Promise<boolean>;
  updateNotifyOnDiscount: (productId: string, notify: boolean) => Promise<void>;
  getNotifyOnDiscount: (productId: string) => boolean;
  lastAlert: FavoritesAlert | null;
  clearAlert: () => void;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export const FavoritesProvider = ({ children }: { children: ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastAlert, setLastAlert] = useState<FavoritesAlert | null>(null);

  const clearAlert = useCallback(() => setLastAlert(null), []);

  // Use stable userId instead of user object to prevent unnecessary re-fetches
  const userId = user?.id;
  
  // Track if initial fetch has been done
  const hasFetchedRef = useRef(false);

  // Reset fetch flag when user changes
  useEffect(() => {
    hasFetchedRef.current = false;
  }, [userId]);

  // Deferred initial fetch - use requestIdleCallback to wait for browser idle
  useEffect(() => {
    if (authLoading) return;
    
    if (!userId) {
      setFavorites([]);
      setLoading(false);
      return;
    }
    
    if (hasFetchedRef.current) return;

    const fetchFavorites = async () => {
      const { data, error } = await supabase
        .from("favorites")
        .select("*")
        .eq("user_id", userId);

      if (error) {
        console.error("Error fetching favorites:", error);
      } else {
        setFavorites(data || []);
      }
      setLoading(false);
    };

    const doFetch = () => {
      hasFetchedRef.current = true;
      fetchFavorites();
    };

    // Use requestIdleCallback if available, fallback to setTimeout
    if ('requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(doFetch, { timeout: 2000 });
      return () => window.cancelIdleCallback(idleId);
    } else {
      const timer = setTimeout(doFetch, 1000);
      return () => clearTimeout(timer);
    }
  }, [userId, authLoading]);

  const isFavorite = useCallback((productId: string) => {
    return favorites.some((f) => f.product_id === productId);
  }, [favorites]);

  const toggleFavorite = useCallback(async (productId: string): Promise<boolean> => {
    if (!user) {
      setLastAlert({ type: 'error', message: 'Inicia sesión para guardar favoritos' });
      return false;
    }

    const existing = favorites.find((f) => f.product_id === productId);

    if (existing) {
      const { error } = await supabase
        .from("favorites")
        .delete()
        .eq("id", existing.id);

      if (error) {
        setLastAlert({ type: 'error', message: 'Error al eliminar de favoritos' });
        return false;
      }

      setFavorites(prev => prev.filter((f) => f.id !== existing.id));
      setLastAlert({ type: 'success', message: 'Eliminado de favoritos' });
      return false;
    } else {
      const { data, error } = await supabase
        .from("favorites")
        .insert({
          user_id: user.id,
          product_id: productId,
          notify_on_discount: true,
        })
        .select()
        .single();

      if (error) {
        setLastAlert({ type: 'error', message: 'Error al agregar a favoritos' });
        return false;
      }

      setFavorites(prev => [...prev, data]);
      setLastAlert({ type: 'success', message: 'Agregado a favoritos' });
      return true;
    }
  }, [user, favorites]);

  const updateNotifyOnDiscount = useCallback(async (productId: string, notify: boolean) => {
    if (!user) return;

    const favorite = favorites.find((f) => f.product_id === productId);
    if (!favorite) return;

    const { error } = await supabase
      .from("favorites")
      .update({ notify_on_discount: notify })
      .eq("id", favorite.id);

    if (error) {
      setLastAlert({ type: 'error', message: 'Error al actualizar preferencia' });
      return;
    }

    setFavorites(prev =>
      prev.map((f) =>
        f.id === favorite.id ? { ...f, notify_on_discount: notify } : f
      )
    );
    setLastAlert({ type: 'success', message: notify ? 'Notificaciones activadas' : 'Notificaciones desactivadas' });
  }, [user, favorites]);

  const getNotifyOnDiscount = useCallback((productId: string) => {
    const favorite = favorites.find((f) => f.product_id === productId);
    return favorite?.notify_on_discount ?? true;
  }, [favorites]);

  return (
    <FavoritesContext.Provider
      value={{
        favorites,
        loading,
        isFavorite,
        toggleFavorite,
        updateNotifyOnDiscount,
        getNotifyOnDiscount,
        lastAlert,
        clearAlert,
      }}
    >
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};
